// src/EmailUnsubscribePage.js
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Alert } from '@mui/material';
import { API_BASE_URL } from './config';

const EmailUnsubscribePage = () => {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [unsubscribed, setUnsubscribed] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        // Register unsubscribe when component mounts
        const registerUnsubscribe = async () => {
            if (!token) {
                setError('Brak tokena w adresie URL');
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                setError(null);
                
                // Collect all query parameters
                const queryParams = {};
                searchParams.forEach((value, key) => {
                    queryParams[key] = value;
                });
                
                const response = await fetch(`${API_BASE_URL}/email/unsubscribe/${token}/`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        query_params: queryParams,
                    }),
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setUnsubscribed(true);
                } else if (response.status === 404) {
                    const errorData = await response.json().catch(() => ({}));
                    setError(errorData.error || 'Nieprawidłowy lub wygasły link. Nie można zlokalizować oferenta.');
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    setError(errorData.error || 'Wystąpił błąd podczas wypisywania z listy.');
                }
            } catch (error) {
                console.error('Failed to unsubscribe:', error);
                setError('Wystąpił błąd podczas połączenia z serwerem.');
            } finally {
                setLoading(false);
            }
        };
        
        registerUnsubscribe();
    }, [token, searchParams]);

    // Show error message if bidder not found or other error
    if (error) {
        return (
            <Box 
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingTop: '5vh',
                    backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '600px',
                        backgroundColor: '#fff',
                        padding: '40px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="/logo.svg"
                        alt="Polfund Logo"
                        sx={{
                            width: 260,
                            marginBottom: 4,
                        }}
                    />
                    
                    <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                        {error}
                    </Alert>

                    <Button
                        variant="contained"
                        onClick={() => window.location.href = 'https://ewadia.pl'}
                        sx={{
                            padding: '12px 32px',
                            backgroundColor: '#E31837',
                            '&:hover': {
                                backgroundColor: '#FF1F3F',
                            },
                            fontSize: '1rem',
                            fontWeight: 500,
                        }}
                    >
                        Przejdź do ewadia.pl
                    </Button>
                </Box>
            </Box>
        );
    }

    // Show loading state
    if (loading) {
        return (
            <Box 
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingTop: '5vh',
                    backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '600px',
                        backgroundColor: '#fff',
                        padding: '40px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="/logo.svg"
                        alt="Polfund Logo"
                        sx={{
                            width: 260,
                            marginBottom: 4,
                        }}
                    />
                    
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: '#666666', 
                            textAlign: 'center',
                        }}
                    >
                        Przetwarzanie...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box 
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingTop: '5vh',
                backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '750px',
                    backgroundColor: '#fff',
                    padding: '40px',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src="/logo.svg"
                    alt="Polfund Logo"
                    sx={{
                        width: 260,
                        marginBottom: 4,
                    }}
                />
                
                <Typography 
                    variant="h4" 
                    component="h1"
                    sx={{ 
                        mb: 3, 
                        color: '#333333',
                        textAlign: 'center',
                        fontWeight: 500,
                    }}
                >
                    Wypisano z listy mailingowej
                </Typography>

                <Typography 
                    variant="body1" 
                    sx={{ 
                        mb: 4, 
                        color: '#666666', 
                        textAlign: 'center',
                        lineHeight: 1.6,
                    }}
                >
                    Twoja zgoda na otrzymywanie wiadomości marketingowych została wycofana. 
                    Nie będziesz już otrzymywać od nas informacji o przetargach i ofertach.
                </Typography>

                <Typography 
                    variant="body2" 
                    sx={{ 
                        mb: 6, 
                        color: '#999999', 
                        textAlign: 'center',
                        lineHeight: 1.6,
                    }}
                >
                    W każdej chwili możesz ponownie wyrazić zgodę kontaktując się z nami.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        width: '100%',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        mt: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => {
                            window.location.href = 'https://ewadia.pl/kontakt/';
                        }}
                        sx={{
                            padding: '16px 32px',
                            backgroundColor: '#E31837',
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: '#FF1F3F',
                            },
                            fontSize: '1rem',
                            fontWeight: 500,
                            minWidth: '250px',
                        }}
                    >
                        <Typography sx={{ color: 'white', fontWeight: 500 }}>
                            Skontaktuj się z Nami
                        </Typography>
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={() => window.location.href = 'https://ewadia.pl'}
                        sx={{
                            padding: '16px 32px',
                            borderRadius: '8px',
                            borderColor: '#E31837',
                            color: '#E31837',
                            '&:hover': {
                                borderColor: '#FF1F3F',
                                backgroundColor: 'rgba(227, 24, 55, 0.04)',
                            },
                            fontSize: '1rem',
                            fontWeight: 500,
                            minWidth: '250px',
                        }}
                    >
                        <Typography sx={{ fontWeight: 500 }}>
                            Przejdź do ewadia.pl
                        </Typography>
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default EmailUnsubscribePage;

